import type { HorizontalAlignment, Rect, VerticalAlignment } from './flex_label.ts';

/** The part of a Font Awesome icon definition that drawing needs: `[width, height, aliases, unicode, path]`. */
export interface IconDefinition {
    iconName: string;
    icon: [number, number, unknown, string, string | string[]];
}

const paths = new WeakMap<IconDefinition, Path2D[]>();
const inks = new WeakMap<IconDefinition, Rect>();

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface IconOptions {
    align?: HorizontalAlignment;
    valign?: VerticalAlignment;
    /**
     * Fit the icon's ink instead of its whole glyph box, as the legacy label did with `Fill`. Icons
     * with little ink in their box (`equals`) come out much larger.
     */
    fill?: boolean;
}

/** Where an icon is placed in a box: the drawn part relative to the box, and the scale and origin of the glyph. */
interface Placement {
    bounds: Rect;
    scale: number;
    originX: number;
    originY: number;
}

/** Resolution of the scratch drawing that finds an icon's ink. */
const INK_SCAN_SIZE = 256;

function shapesOf(icon: IconDefinition) : Path2D[] {
    let shapes = paths.get(icon);
    if (!shapes) {
        const pathData = icon.icon[4];
        shapes = (Array.isArray(pathData) ? pathData : [pathData]).map((path) => new Path2D(path));
        paths.set(icon, shapes);
    }

    return shapes;
}

/**
 * The part of the glyph box that has ink, in glyph units: found once per icon by drawing it on a
 * small scratch canvas and scanning it. The whole box where no canvas exists (tests).
 */
function iconInk(icon: IconDefinition) : Rect {
    const cached = inks.get(icon);
    if (cached) {
        return cached;
    }

    const [width, height] = icon.icon;
    const whole = { left: 0, top: 0, width, height };
    if (typeof OffscreenCanvas === 'undefined' || typeof Path2D === 'undefined') {
        return whole;
    }

    const scale = INK_SCAN_SIZE / Math.max(width, height);
    const pixelWidth = Math.ceil(width * scale);
    const pixelHeight = Math.ceil(height * scale);
    const ctx = new OffscreenCanvas(pixelWidth, pixelHeight).getContext('2d');
    if (!ctx) {
        return whole;
    }

    ctx.scale(scale, scale);
    for (const shape of shapesOf(icon)) {
        ctx.fill(shape);
    }
    const alpha = ctx.getImageData(0, 0, pixelWidth, pixelHeight).data;
    let minX = pixelWidth;
    let minY = pixelHeight;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < pixelHeight; y++) {
        for (let x = 0; x < pixelWidth; x++) {
            if ((alpha[(y * pixelWidth + x) * 4 + 3] ?? 0) > 0) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }
    }

    const ink = maxX < 0 ? whole : { left: minX / scale, top: minY / scale, width: (maxX + 1 - minX) / scale, height: (maxY + 1 - minY) / scale };
    inks.set(icon, ink);

    return ink;
}

function place(icon: IconDefinition, box: Rect, options: IconOptions) : Placement {
    const [glyphWidth, glyphHeight] = icon.icon;
    const fitted = options.fill ? iconInk(icon) : { left: 0, top: 0, width: glyphWidth, height: glyphHeight };
    const scale = Math.min(box.width / fitted.width, box.height / fitted.height);
    const width = fitted.width * scale;
    const height = fitted.height * scale;
    const align = options.align ?? 'center';
    const valign = options.valign ?? 'center';
    const left = align === 'left' ? 0 : align === 'center' ? (box.width - width) / 2 : box.width - width;
    const top = valign === 'top' ? 0 : valign === 'center' ? (box.height - height) / 2 : box.height - height;

    return { bounds: { left, top, width, height }, scale, originX: left - fitted.left * scale, originY: top - fitted.top * scale };
}

/** Where an icon draws in `box` when it is as large as fits, relative to the box (its render bounds). */
export function iconPlacement(icon: IconDefinition, box: Rect, options: IconOptions = {}) : Rect {
    return place(icon, box, options).bounds;
}

/**
 * Draws an icon as large as fits in `box`, from its SVG path data (so it is sharp at any size and
 * needs no icon font). Returns where it went, relative to the box.
 */
export function drawIcon(ctx: Context, icon: IconDefinition, box: Rect, color: string, options: IconOptions = {}) : Rect {
    const placement = place(icon, box, options);
    ctx.save();
    ctx.translate(box.left + placement.originX, box.top + placement.originY);
    ctx.scale(placement.scale, placement.scale);
    ctx.fillStyle = color;
    for (const shape of shapesOf(icon)) {
        ctx.fill(shape);
    }
    ctx.restore();

    return placement.bounds;
}